<template>
  <div :class="`orderlist-container ${source}`">
    <template v-if="source !== 'pending'">
      <div v-if="getTickets?.[source]?.length">
        <div class="sale-group-container" v-for="(sale, saleKey) in getTickets[source]" :key="saleKey">
          <div class="separator" v-if="saleKey > 0"></div>
          <div class="cupom-details">
            <div class="cupom-number">Cupom: {{ sale.saleSeq }}</div>
            <div class="export-cupom" @click="sendCouponByEmail(sale.saleSeq)">
              Enviar cupom fiscal por e-mail
              <div class="icon">
                <fa :icon="['fas', 'arrow-up-right-from-square']" />
              </div>
            </div>
          </div>
          <div class="prod-container" v-for="(item, itemKey) in sale.items" :key="itemKey" :data-qrcode-key="item.QRCODE_KEY">
            <div class="desc">
              <div class="cupom-description">
                <div class="cupom">
                  <span>Cupom:</span>
                  <span v-text="sale.saleSeq"></span>
                </div>
                <div class="item-number">
                  <span>Item:</span>
                  <span>{{ item.itemNumber }}/{{ item.totalItemsInSale }}</span>
                </div>
              </div>
              <div class="desc-items">
                <div class="label">
                  <h2 v-text="item.NAME"></h2>
                  <span v-if="item.ORDER_TIME" class="purchase-date">Adquirido em: {{ formatPurchaseDate(item.ORDER_TIME) }}</span>
                  <span class="prod-desc" v-text="item.DESCRIPTION?.filter(d => d.trim()).join(', ')"></span>
                </div>
                <div class="qrcode" v-if="item.QRCODE_KEY" :class="{ zoomed: item.zoomed }" @click="qrCodeClicked(item)">
                  <img :src="item.QRCODE_KEY" :alt="item.QRCODE_KEY">
                </div>
              </div>
            </div>
            <div :class="`olcfoter ${item.STATUS?.toLowerCase()}`">
              <span v-text="label[item.STATUS]"></span>
              <span>
                <span v-text="item.REDEEMED_TIME || item.EXPIRATION_TIME"></span>
                <fa v-if="item.STATUS?.toLowerCase() === 'unavailable'" :icon="['fas', 'check']" />
                <fa v-else-if="item.STATUS?.toLowerCase() === 'expired'" :icon="['fas', 'x']" />
                <fa v-else :icon="['fas', 'tag']" />
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div v-if="showOptimisticProcessing">
          <div class="optimistic-feedback">
            <span>Seu pedido foi recebido!</span>
            <span>Estamos preparando tudo e ele logo aparecerá em "Disponíveis".</span>
          </div>
          <ProcessingLoader v-if="!isLoading" />
        </div>
        <div v-else class="emptyOrders">
          <span v-if="!isLoading">Nada aqui, por enquanto.</span>
          <span v-else>Carregando pedidos...</span>
        </div>
      </div>
    </template>

    <template v-else>
      <div v-if="getTickets?.pending?.length">
        <div class="venda-container" v-for="(venda, key) in getTickets.pending" :key="key">
          <span class="date-venda" v-text="venda.date"></span>
          <template v-for="(item, vkey) in venda.items" :key="vkey">
            <div class="row">
              <span class="prod-qnt" v-text="`${item.quantity}x`"></span>
              <span class="prod-name" v-text="item.name"></span>
              <span class="prod-unpr" v-text="item.value"></span>
            </div>
            <div class="row2" v-if="item.observations?.length">
              <span class="prod-desc" v-text="item.observations.filter(o => o.obsName && o.obsName.trim()).map(o => o.obsName.trim()).join(', ')"></span>
            </div>
          </template>
          <div class="footer-row">
            <span class="status" v-text="'Processando'"></span>
            <span class="total" v-text="venda.value"></span>
          </div>
        </div>
      </div>

      <div v-else>
        <div v-if="showOptimisticProcessing">
          <div class="optimistic-feedback">
            <span>Seu pedido foi recebido!</span>
            <span>Estamos preparando tudo e ele logo aparecerá em "Disponíveis".</span>
          </div>
          <ProcessingLoader v-if="!isLoading" />
        </div>
        <div v-else class="emptyOrders">
          <span v-if="!isLoading">Nenhum pedido processando no momento.</span>
          <span v-else>Carregando pedidos...</span>
        </div>
      </div>
    </template>
  </div>
  <ScreenLoader :isLoading="isLoading" />
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import util from '@/assets/libs/util'
import { sendCouponByEmail } from '@/assets/controllers/PaymentController'
import ScreenLoader from './loader/ScreenLoader.vue'
import ProcessingLoader from './loader/ProcessingLoader.vue'

export default {
  name: 'OrderListComp',
  props: ['source'],
  components: {
    ScreenLoader,
    ProcessingLoader
  },
  data () {
    return {
      pollingTimer: null,
      pollingActive: false,
      isNewOrder: false,
      label: {
        AVAILABLE: 'Validade',
        UNAVAILABLE: 'Consumido',
        EXPIRED: 'Vencido'
      },
      isLoading: false
    }
  },
  computed: {
    ...mapGetters(['getTickets', 'getAppDefinitions']),
    hasAvailableTickets () {
      return (this.getTickets?.available?.length || 0) > 0
    },
    showOptimisticProcessing () {
      return this.isNewOrder && !this.hasAvailableTickets
    }
  },
  async mounted () {
    this.isNewOrder = this.$route.query?.newOrder === 'true'

    const userToken = this.$route.query?.user
    if (userToken) {
      util.setLocalVar('SESSION_TOKEN', userToken)
    }
    const currentUserToken = localStorage.getItem('SESSION_TOKEN')
    let lastTicketToken = localStorage.getItem('LAST_TICKET_TOKEN')
    if (!lastTicketToken) {
      localStorage.setItem('LAST_TICKET_TOKEN', currentUserToken)
      lastTicketToken = currentUserToken
    }
    if (currentUserToken && lastTicketToken && currentUserToken !== lastTicketToken) {
      await this.clearTickets()
      localStorage.setItem('LAST_TICKET_TOKEN', currentUserToken)
    }

    this.isLoading = true
    await this.loadTickets()
    this.isLoading = false

    this.startPolling()
  },
  beforeUnmount () {
    this.pollingActive = false
    clearTimeout(this.pollingTimer)
  },
  methods: {
    ...mapActions(['loadTickets', 'clearTickets']),
    startPolling () {
      clearTimeout(this.pollingTimer)
      this.pollingActive = true

      const pollingInterval = 30000
      const getItemKeys = (status) => {
        return new Set(
          Object.values(this.getTickets?.[status] || {})
            .flatMap(sale => (sale.items || [])
              .map(item => `${sale.saleSeq}:${item.SEQUENTIAL}`))
        )
      }

      let lastAvailableItemKeys = getItemKeys('available')
      let lastUnavailableItemKeys = getItemKeys('unavailable')

      const poll = async () => {
        try {
          await this.loadTickets()

          const currentAvailableItemKeys = getItemKeys('available')
          const currentUnavailableItemKeys = getItemKeys('unavailable')
          const hasNewAvailableItem = [...currentAvailableItemKeys]
            .some(key => !lastAvailableItemKeys.has(key))
          const hasNewUnavailableItem = [...currentUnavailableItemKeys]
            .some(key => !lastUnavailableItemKeys.has(key))

          if (this.source === 'pending' && hasNewAvailableItem) {
            this.$emit('status-changed', 'available')
          } else if (this.source === 'available' && hasNewUnavailableItem) {
            this.$emit('status-changed', 'unavailable')
          }

          lastAvailableItemKeys = currentAvailableItemKeys
          lastUnavailableItemKeys = currentUnavailableItemKeys
        } finally {
          if (this.pollingActive) {
            this.pollingTimer = setTimeout(poll, pollingInterval)
          }
        }
      }

      this.pollingTimer = setTimeout(poll, pollingInterval)
    },
    qrCodeClicked: function (item) {
      if (this.source !== 'unavailable' && item.STATUS !== 'UNAVAILABLE') {
        item.zoomed = !item.zoomed
      }
    },
    formatPurchaseDate: function (purchaseDate) {
      return String(purchaseDate || '').split(' ')[0]
    },
    sendCouponByEmail: function (saleSeq) {
      const logo = this.getAppDefinitions?.appLogo || null
      this.isLoading = true
      sendCouponByEmail(saleSeq, logo)
        .then((res) => {
          if (res.error) {
            util.showErrorNotification({ title: res.message || 'Erro ao enviar cupom' })
            return
          }
          util.showSuccessNotification({ title: 'Cupom enviado com sucesso!' })
        })
        .catch(() => {
          util.showErrorNotification({ title: 'Erro ao enviar cupom. Tente novamente mais tarde.' })
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<style lang="less">
.optimistic-feedback {
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 8px;
  padding: 15px;
  margin: 10px 0 20px 0;
  text-align: center;

  span {
    display: block;
    &:first-child {
      font-weight: bold;
      margin-bottom: 5px;
    }
  }
}

.emptyOrders {
  display: block;
  text-align: center;
  padding: 15px 0;
  background-color: #d3d3d34a;
}
.orderlist-container {
  &.unavailable {
    .qrcode {
      img, canvas {
        filter: blur(5px) invert(1);
      }
    }
  }

  .separator {
    height: 1px;
    background-color: #a7a7a7;
    margin: 10px 0;
  }

  .cupom-details {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 5px;
    padding: 10px 0;

    .cupom-number {
      font-weight: 600;
      font-size: 14px;
    }

    .export-cupom {
      cursor: pointer;
      color: var(--primaryColor);
      font-size: 14px;
      display: grid;
      grid-template-columns: 120px 20px;
      gap: 5px;

      .icon {
        display: flex;
        align-items: center;
        fa {
          margin-left: 5px;
        }
      }
    }
  }

  .prod-container {
    margin-bottom: 20px;

    .desc {
      padding: 20px;
      border-top-right-radius: 16px;
      border-top-left-radius: 16px;
      border: solid 1px #E9E9E9;
      border-bottom: none;

      .desc-items {
        display: grid;
        grid-template-columns: calc(100% - 130px) 120px;
        grid-column-gap: 20px;

        h2 {
          margin: 0;
          padding: 0;
          font-size: 16px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        span.prod-desc {
          display: -webkit-box;
          -webkit-line-clamp: 2;
        }
        .purchase-date {
          display: block;
          margin-top: 4px;
          color: #717171;
          font-size: 12px;
          line-height: 1.3;
        }
        .qrcode {
          img, canvas {
            top: 0;
            right: 0;
            width: 120px;
            transition: .3s linear right;
            position: relative;
            display: block;
            max-width: 100%;
            object-fit: contain;
          }
          &:before {
            content: '';
            position: fixed;
            width: 100vw;
            height: var(--real-vh);
            top: 0;
            left: 0;
            background: #000;
            opacity: 0;
            pointer-events: none;
            transition: .3s linear opacity;
            z-index: 999;
          }
          &.zoomed {
            z-index: 999;
          img, canvas {
            z-index: 999;
            width: calc(100% - 40px);
            position: fixed;
            right: 50%;
            top: 50%;
            transform: translate(50%, -50%);
            background: white;
            padding: 10px;
          }
            &:before {
              pointer-events: auto;
              opacity: .9;
            }
            img, canvas {
              margin: auto;
            }
          }
        }
      }
    }

    .cupom-description {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      .cupom, .item-number {
        display: flex;
        flex-direction: column;
        span {
          font-size: 12px;
          color: gray;
        }
      }
    }

    .olcfoter {
      padding: 20px;
      position: relative;
      border: solid 1px #E9E9E9;
      border-top: dashed 4px #E9E9E9;
      border-bottom-right-radius: 16px;
      border-bottom-left-radius: 16px;
      display: grid;
      grid-template-columns: 50px auto;
      &:before, &:after {
        content: '';
        position: absolute;
        top: 0;
        width: 30px;
        height: 15px;
        background-color: white;
        border-radius: 20px 20px 0 0;
        border: solid 1px #E9E9E9;
        border-bottom: none;
      }
      &:before {
        transform: rotate(90deg);
        left: -9.0px;
        top: -10px;
      }
      &:after {
        transform: rotate(-90deg);
        right: -9.0px;
        top: -10px;
      }
      span {
        color: var(--primaryColor);
        &:last-of-type {
          text-align: right;
        }
      }
      svg {
        margin-left: 15px;
      }
      &.unavailable span{
        color: green;
      }
      &.expired span{
        color: red;
      }
    }
  }
  .venda-container {
    border: 1px solid rgba(0, 0, 0, 0.18);
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
    position: relative;
    display: block;
    padding: 10px;
    margin-bottom: 15px;

    span {
      display: block;
      &.date-venda {
        margin-bottom: 10px;
        color: gray;
      }
      &.prod-unpr, &.total {
        text-align: right;
      }
      &.total {
        font-weight: bold;
      }
      &.prod-desc {
        color: gray;
        margin-bottom: 15px;
        max-width: 70%;
      }
      &.status {
        color: orange;
        font-weight: bold;
        display: inline-block;
        vertical-align: middle;
        &:before{
          vertical-align: middle;
          display: inline-block;
          background: orange;
          border-radius: 100%;
          content: '';
          width: 15px;
          height: 15px;
          animation: blink 2s infinite;
          margin: 0 10px 2px 0;
        }

        @keyframes blink {
          0%, 100% {
            opacity: 1; /* Totalmente visível */
          }
          50% {
            opacity: 0; /* Invisível */
          }
        }
      }

    }
    .row {
      display: grid;
      grid-template-columns: 20px auto 100px;
      font-size: 14px;
    }

    .row2 {
      font-size: 12px;
      width: -webkit-fill-available;

      .prod-desc {
        display: inline-block;
        color: gray;
        margin: 2px 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
    }

    .footer-row {
      display: grid;
      margin-top: 20px;
      grid-template-columns: auto auto;
    }
  }
}
</style>
